import { Box, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import METRIC_DEFINITIONS from './metricDefinitions';

// A formula line, styled apart from the plain-English description (monospace, bold) so the two read as
// distinct things - "what it means" vs. "how it's actually computed" - rather than running together.
const FormulaLine = ({ formula }) => (
  <Typography
    component="div"
    variant="caption"
    sx={{ fontFamily: 'monospace', fontWeight: 700, color: '#4A3190', mt: 0.25 }}
  >
    {formula}
  </Typography>
);

// Always-visible (not a hover tooltip - easy to miss for someone who isn't already familiar with the metric)
// definition box, meant to sit directly inside a config screen's add/edit form, right where the person is
// about to type a number, not just once at the top of the page. `items` (array of metric keys) renders a
// short bulleted list instead of one paragraph - for the few shared config screens (Target Slab, Project
// Threshold) that set several metrics' thresholds in a single form. Each metric's entry in
// metricDefinitions.js carries both a plain-English `description` and its `formula`; `text` lets a caller
// override the description only (the formula still comes from the dictionary, when one exists for that key).
const MetricDefinitionNote = ({ metricKey, text, items }) => {
  if (items?.length) {
    return (
      <Box display="flex" gap={1} alignItems="flex-start" sx={{ p: 1.5, bgcolor: '#EDE7F8', borderRadius: 1.5 }}>
        <Icon icon="solar:info-circle-bold" width={18} height={18} style={{ marginTop: 2, flexShrink: 0, color: '#4A3190' }} />
        <Box component="ul" sx={{ m: 0, pl: 2.5, color: '#4A3190' }}>
          {items.map((key) => (
            METRIC_DEFINITIONS[key] ? (
              <Typography key={key} component="li" variant="body2" sx={{ color: '#4A3190', mb: 0.75 }}>
                {METRIC_DEFINITIONS[key].description}
                {METRIC_DEFINITIONS[key].formula && <FormulaLine formula={METRIC_DEFINITIONS[key].formula} />}
              </Typography>
            ) : null
          ))}
        </Box>
      </Box>
    );
  }

  const entry = METRIC_DEFINITIONS[metricKey];
  const description = text || entry?.description;
  if (!description) return null;
  return (
    <Box display="flex" gap={1} alignItems="flex-start" sx={{ p: 1.5, bgcolor: '#EDE7F8', borderRadius: 1.5 }}>
      <Icon icon="solar:info-circle-bold" width={18} height={18} style={{ marginTop: 2, flexShrink: 0, color: '#4A3190' }} />
      <Box>
        <Typography variant="body2" sx={{ color: '#4A3190' }}>{description}</Typography>
        {entry?.formula && <FormulaLine formula={entry.formula} />}
      </Box>
    </Box>
  );
};

export default MetricDefinitionNote;
